import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import {usePalletRequestPreview} from '@/api/warehouse'
import type {WarehouseCompanyFilter} from '@/lib/warehouse-company'
// src/pages/warehouse/components/AuftragAnlegenDialog.tsx
// Modul 25: „Auftrag anlegen" im Reiter Kommissionieren — Ware gezielt herausholen
// lassen, auch ohne Handregal-Sticker in der Hand.
//
// Gestaltung: Projektmuster (shadcn/Dialog, bestehende Tokens, keine neuen
// Komponenten) — mit EINER bewussten Betonung: die Kartonzahl steht gross zwischen
// den beiden Stellern, wie die Mengen-Kacheln der Scan-App (app/nachschub.tsx).
// Begruendung fuer die Ausnahme in einer sonst dichten Verwaltungsmaske: es sind
// dieselben Leute wie am Handgeraet, und eine falsch gesetzte Menge zieht eine echte
// Fehlentnahme in der Halle nach sich — diese Zahl muss man im Vorbeigehen lesen.
//
// Ablauf: Nummer eintippen -> Vorschau (Artikelname, offene Anforderung, Bestand)
// -> Menge stellen -> anlegen. Der Dialog bleibt nach dem Anlegen offen und leert
// das Nummernfeld, weil in der Praxis mehrere Auftraege hintereinander entstehen.
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useFillupVorschau, useCreateFillupRequest,
  FILLUP_CODE_MIN_LAENGE, FILLUP_CODE_MAX_LAENGE as CODE_MAX_LAENGE,
} from '@/api/warehouse'
import {
  klemmeKartons,
  deuteErgebnis, palletSourceText, KARTONS_MIN, KARTONS_MAX, type Meldung,
} from '@/lib/fillup-anforderung'
import { neueUuid } from '@/lib/uuid'
import { useAuth } from '@/api/auth'
import { ApiError } from '@/api/client'
import { useQueryClient } from '@tanstack/react-query'
import type { FillupAnforderungEingabe } from '@/api/warehouse'

/** Wartezeit nach dem letzten Tastendruck, bevor die Vorschau geholt wird —
 *  sonst eine Abfrage pro Zeichen. */
export const VORSCHAU_VERZOEGERUNG_MS = 300

function CartonAuftragAnlegenDialog({ company }: { company?: WarehouseCompanyFilter }) {
  const { user } = useAuth()
  const storageKey = `stahlia_fillup_pending:${user?.id}`
  const [pending, setPending] = useState<FillupAnforderungEingabe | null>(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) ?? 'null') as FillupAnforderungEingabe | null } catch { return null }
  })
  const [offen, setOffen] = useState(false)
  const [verwerfenOffen, setVerwerfenOffen] = useState(false)
  const [konflikt, setKonflikt] = useState(false)
  const [listeLaedt, setListeLaedt] = useState(false)
  const listeLaedtRef = useRef(false)
  const qc = useQueryClient()
  const [nummer, setNummer] = useState('')
  const [gepruefteNummer, setGepruefteNummer] = useState('')
  const [kartonsText, setKartonsText] = useState(String(KARTONS_MIN))
  const [meldung, setMeldung] = useState<Meldung | null>(null)

  // Zweiter Riegel gegen den Doppelklick, unabhaengig vom Render-Takt
  // (Muster busyRef in stahlia-scan-app/src/app/nachschub.tsx).
  const sendenRef = useRef(false)
  // Zaehlt die Maskensitzungen — siehe schliessen().
  const sitzungRef = useRef(0)

  const vorschau = useFillupVorschau(gepruefteNummer, company)
  const anlegen = useCreateFillupRequest()
  function freigeben(request: FillupAnforderungEingabe) {
    try {
      const gespeicherter = JSON.parse(localStorage.getItem(storageKey) ?? 'null') as FillupAnforderungEingabe | null
      if (gespeicherter?.request_id === request.request_id) localStorage.removeItem(storageKey)
    } catch { /* Bei nicht lesbarem Speicher keine andere Anforderung löschen. */ }
    setPending(alt => alt?.request_id === request.request_id ? null : alt)
  }

  const kartons = klemmeKartons(Number(kartonsText))

  useEffect(() => {
    const id = setTimeout(() => setGepruefteNummer(nummer.trim()), VORSCHAU_VERZOEGERUNG_MS)
    return () => clearTimeout(id)
  }, [nummer])

  function stelleKartons(delta: number) {
    setKartonsText(String(klemmeKartons(kartons + delta)))
  }

  function alleFelderZuruecksetzen() {
    setNummer('')
    setGepruefteNummer('')
    setKartonsText(String(KARTONS_MIN))
    setMeldung(null)
    // Ein ungeklärter Request bleibt beim Schließen erhalten.
  }

  /** EINZIGER Schliess-Weg. Wichtig: bei einem gesteuerten Dialog ruft Radix
   *  `onOpenChange` NUR bei seinen eigenen Wegen (Escape, Klick daneben, X). Ein
   *  eigener Knopf, der bloss `setOffen(false)` macht, laesst die Maske ungeleert
   *  stehen — beim naechsten Oeffnen stuenden Nummer und alte Meldung wieder da. */
  function schliessen() {
    setOffen(false)
    // Neue Maskensitzung: eine noch laufende Anforderung darf ihre Meldung und
    // ihr Feld-Leeren nicht mehr in die naechste Sitzung schreiben (Codex-Review
    // 2026-08-04, Befund 2).
    sitzungRef.current += 1
    alleFelderZuruecksetzen()
  }

  async function verwerfenVorbereiten() {
    if (listeLaedtRef.current || anlegen.isPending) return
    listeLaedtRef.current = true
    setListeLaedt(true)
    const sitzung = sitzungRef.current
    try {
      await Promise.all([
        qc.refetchQueries({ queryKey: ['warehouse', 'fillup', 'overview'], type: 'active' }, { throwOnError: true }),
        qc.refetchQueries({ queryKey: ['warehouse', 'pallet', 'overview'], type: 'active' }, { throwOnError: true }),
      ])
      if (sitzungRef.current === sitzung) setVerwerfenOffen(true)
    } catch {
      if (sitzungRef.current === sitzung) setMeldung({ art: 'fehler', text: 'Liste konnte nicht aktualisiert werden. Bitte erneut versuchen.' })
    } finally {
      listeLaedtRef.current = false
      setListeLaedt(false)
    }
  }

  // Die getippte Nummer ist noch nicht geprueft, solange die Verzoegerung laeuft —
  // in diesem Moment gehoert die angezeigte Vorschau noch zur VORIGEN Eingabe.
  const vorschauPasst = gepruefteNummer === nummer.trim()
  const artikel = vorschauPasst ? vorschau.data : undefined
  const pallet = artikel?.request_kind === 'pallet'
  // Das Backend begrenzt `code` auf 40 Zeichen (fillupRequestSchema). Die Vorschau
  // kennt diese Grenze nicht — ohne eigene Pruefung liefe der Bediener in ein
  // nacktes HTTP 400 (Codex-Review 2026-08-04, Befund 3).
  const nummerZuLang = !!artikel && artikel.merchant_sku.length > CODE_MAX_LAENGE
  const kannAnlegen = (!!pending || (!!artikel && !nummerZuLang)) && !anlegen.isPending && !listeLaedt

  async function auftragAnlegen() {
    if (sendenRef.current || (!pending && (!artikel || nummerZuLang))) return
    sendenRef.current = true
    // Sitzung merken: trifft die Antwort erst ein, nachdem der Bediener die Maske
    // geschlossen und neu geoeffnet hat, darf sie dessen neue Eingabe nicht mehr
    // anfassen. Die Buchfuehrung ueber die Idempotenz-Id laeuft trotzdem weiter.
    const sitzung = sitzungRef.current
    const vorherUngeklaert = pending !== null
    setKonflikt(false)
    let request: FillupAnforderungEingabe | null = null
    try {
      // Pallet preview returns the family SKU; retain the typed Netze R dimension/alias.
      // Ordinary cartons keep the previously resolved merchant SKU.
      request = pending ?? { code: pallet ? nummer.trim() : artikel!.merchant_sku, cartons: pallet ? 1 : kartons, request_id: neueUuid(), ...(company && company !== 'both' ? { company } : {}) }
      // Vor dem Senden sichern, damit auch ein Wechsel des Reiters den Request erhält.
      localStorage.setItem(storageKey, JSON.stringify(request))
      setPending(request)
      const ergebnis = await anlegen.mutateAsync(request)
      const menge = request.cartons
      const neueMeldung = deuteErgebnis(ergebnis, menge)
      const nochDieselbeSitzung = sitzungRef.current === sitzung

      if (neueMeldung.art === 'fehler') {
        if (nochDieselbeSitzung) setMeldung(neueMeldung)
        toast.error(neueMeldung.text)
        return // Dialog bleibt stehen, damit der Bediener die Eingabe korrigieren kann
      }
      const erfolg = vorherUngeklaert ? { art: 'ok' as const, text: 'Auftrag ist angelegt' } : neueMeldung
      toast.success(erfolg.text)
      // Ein bestätigtes Ergebnis gibt diesen Request wieder frei.
      freigeben(request)
      if (!nochDieselbeSitzung) return
      setMeldung(erfolg)
      setNummer('')
      setGepruefteNummer('')
      setKartonsText(String(KARTONS_MIN))
    } catch (err) {
      if (vorherUngeklaert && err instanceof ApiError && !err.undecodable && err.status === 409) setKonflikt(true)
      if (request && err instanceof ApiError && !err.undecodable &&
        (err.status === 400 || err.status === 422 || (err.status === 409 && !vorherUngeklaert))) {
        freigeben(request)
      }
      // Ausgang unklar (Netz/Server) — Vorgang und Id bleiben stehen, die
      // Wiederholung sendet dieselbe Id und kann nichts doppelt anlegen.
      const text = err instanceof Error ? err.message : 'Anlegen fehlgeschlagen'
      if (sitzungRef.current === sitzung) setMeldung({ art: 'fehler', text })
      toast.error(text)
    } finally {
      sendenRef.current = false
    }
  }

  return (
    <Dialog
      open={offen}
      onOpenChange={(o) => { if (o) setOffen(true); else schliessen() }}
    >
      <DialogTrigger asChild>
        <Button size="sm">Auftrag anlegen</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Auftrag anlegen{pending ? ` · ${pending.company === 'priostahl' ? 'Priostahl' : pending.company === 'stahlia' ? 'Stahlia' : 'Beide'}` : company && company !== 'both' ? ` · ${company === 'priostahl' ? 'Priostahl' : 'Stahlia'}` : ''}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="anlegen-nummer" className="text-sm font-medium">Nummer</label>
            {/* Waehrend des Sendens gesperrt: der Erfolgszweig leert das Feld, und
                eine in der Zwischenzeit getippte naechste Nummer waere sonst weg
                (Gemini-Review 2026-08-04, Befund 2A). */}
            <Input
              id="anlegen-nummer"
              value={pending?.code ?? nummer}
              autoComplete="off"
              inputMode="numeric"
              placeholder="Sternnummer, z. B. 1234"
              disabled={anlegen.isPending || !!pending}
              onChange={(e) => setNummer(e.target.value)}
            />
            <p className="text-xs text-muted">
              Die Sternnummer des Artikels — dieselbe Nummer wie auf dem Handregal-Sticker.
            </p>
          </div>

          {/* Vorschau: Artikelname und, falls vorhanden, die bereits offene Anforderung.
              Ohne diese Zeile legt der Bediener unwissentlich obendrauf. */}
          {pending ? <div role="status" className="rounded-lg border border-line p-3 text-sm">Ergebnis ungeklärt: {pending.code} · {pending.cartons} · {pending.company ?? 'Beide'}. Ursprünglichen Auftrag erneut senden.</div> : <div className="min-h-[3.5rem] rounded-lg border border-line bg-panel p-3 text-sm">
            {nummer.trim().length < FILLUP_CODE_MIN_LAENGE ? (
              <span className="text-muted">Nummer eingeben, dann wird der Artikel angezeigt.</span>
            ) : !vorschauPasst || vorschau.isFetching ? (
              <span className="text-muted">Nummer wird geprüft…</span>
            ) : vorschau.isError ? (
              <span className="text-red-600">
                {vorschau.error instanceof Error ? vorschau.error.message : 'Nummer konnte nicht geprüft werden.'}
              </span>
            ) : artikel ? (
              <div className="space-y-1">
                <div>
                  <b><GrundartikelLink code={artikel.merchant_sku} /></b>
                  {artikel.artikel_name ? <> — {artikel.artikel_name}</> : null}
                </div>
                <div className="text-xs text-muted">
                  Bestand am Platz: {artikel.has_stock ? 'ja' : 'nein — der Auftrag wird vorgemerkt'}
                </div>
                {nummerZuLang && (
                  <div className="rounded-md border border-red-500 bg-red-50 px-2 py-1 text-xs font-medium text-red-700">
                    Diese Artikelnummer ist mit {artikel.merchant_sku.length} Zeichen zu lang —
                    das Lager nimmt höchstens {CODE_MAX_LAENGE}. Bitte beim Leiter melden.
                  </div>
                )}
                {pallet && <div><p>1 ganze Palette · {palletSourceText(artikel.source_lager)} · Ziel Lager {artikel.target_lager}</p><p>Die eingegebene Kartonzahl wird ignoriert; es wird eine ganze Palette angefordert.</p></div>}
                {!pallet && artikel.open_request && (
                  <div className="rounded-md border border-amber-500 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900">
                    Bereits offen: {artikel.open_request.cartons} Karton(s) ({artikel.open_request.status})
                    {' — '}die neue Menge wird addiert.
                  </div>
                )}
              </div>
            ) : null}
          </div>}

          {!pallet && <div className="space-y-1.5">
            <label htmlFor="anlegen-kartons" className="text-sm font-medium">Kartons</label>
            <div className="flex items-center gap-3">
              <Button
                type="button" variant="outline" size="icon" aria-label="Ein Karton weniger"
                disabled={anlegen.isPending || !!pending || kartons <= KARTONS_MIN}
                onClick={() => stelleKartons(-1)}
              >
                −
              </Button>
              <Input
                id="anlegen-kartons"
                className="h-12 w-24 text-center text-3xl font-bold tabular-nums"
                value={pending ? String(pending.cartons) : kartonsText}
                inputMode="numeric"
                disabled={anlegen.isPending || !!pending}
                onChange={(e) => setKartonsText(e.target.value)}
                onBlur={() => setKartonsText(String(kartons))}
              />
              <Button
                type="button" variant="outline" size="icon" aria-label="Ein Karton mehr"
                disabled={anlegen.isPending || !!pending || kartons >= KARTONS_MAX}
                onClick={() => stelleKartons(1)}
              >
                +
              </Button>
            </div>
          </div>}
          {meldung && (
            <div
              role="status"
              className={`rounded-lg border p-3 text-sm ${
                meldung.art === 'fehler'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : meldung.art === 'hinweis'
                    ? 'border-amber-500 bg-amber-50 text-amber-900'
                    : 'border-emerald-500 bg-emerald-50 text-emerald-800'
              }`}
            >
              {meldung.text}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={schliessen}>Fertig</Button>
          {pending && <Button variant="outline" disabled={anlegen.isPending || listeLaedt} onClick={() => void verwerfenVorbereiten()}>{listeLaedt ? 'Liste wird geladen…' : konflikt ? 'Verwerfen' : 'Trotzdem verwerfen'}</Button>}
          <Button disabled={!kannAnlegen} onClick={() => void auftragAnlegen()}>
            {anlegen.isPending ? 'Wird angelegt…' : pending ? 'Status klären' : 'Auftrag anlegen'}
          </Button>
        </DialogFooter>
        <Dialog open={verwerfenOffen} onOpenChange={setVerwerfenOffen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Auftrag verwerfen?</DialogTitle>
              <DialogDescription>Vorher in der Liste prüfen, ob der Auftrag schon angelegt ist.</DialogDescription>
              {!konflikt && <p className="text-sm font-medium text-amber-700">Kann doppelte Aufträge erzeugen</p>}
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setVerwerfenOffen(false)}>Abbrechen</Button>
              <Button disabled={anlegen.isPending} onClick={() => {
                if (pending) freigeben(pending)
                setVerwerfenOffen(false)
                alleFelderZuruecksetzen()
              }}>Auftrag verwerfen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  )
}

export function AuftragAnlegenDialog(
  props: {
    requestKind?: 'cartons' | 'pallet'
    company?: WarehouseCompanyFilter
    enabled?: boolean
    pending?: boolean
    onPalletRequest?: (code: string, roll?: number) => Promise<boolean>
  } = {},
) {
  return props.requestKind === 'pallet' ? (
    <PalletAuftragAnlegenDialog {...props} />
  ) : (
    <CartonAuftragAnlegenDialog company={props.company} />
  )
}
function PalletAuftragAnlegenDialog({
  company = 'stahlia',
  enabled = false,
  pending = false,
  onPalletRequest,
}: {
  company?: WarehouseCompanyFilter
  enabled?: boolean
  pending?: boolean
  onPalletRequest?: (code: string, roll?: number) => Promise<boolean>
}) {
  const [open, setOpen] = useState(false),
    [code, setCode] = useState(''),
    [r, setR] = useState(''),
    [notice, setNotice] = useState('')
  const roll = r === '' ? undefined : Number(r),
    preview = usePalletRequestPreview(code, company, roll)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={!enabled || pending}>1 ganze Palette anfordern</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Palettenauftrag{company !== 'both' ? ` · ${company === 'priostahl' ? 'Priostahl' : 'Stahlia'}` : ''}</DialogTitle>
        </DialogHeader>
        <Input
          aria-label="Palettenartikel"
          placeholder="PL-Artikel / Grundartikel"
          value={code}
          onChange={(e) => {
            setCode(e.target.value)
            setNotice('')
          }}
        />
        <label>
          R für Netze
          <select aria-label="R in Metern" value={r} onChange={(e) => setR(e.target.value)}>
            <option value="">PL / aus Artikel</option>
            <option value="1">R1</option>
            <option value="2">R2</option>
            <option value="5">R5</option>
            <option value="10">R10</option>
            <option value="30">R30</option>
          </select>
        </label>
        {preview.isError && <p role="alert">Artikel oder R prüfen.</p>}
        {preview.data && (
          <div>
            <b><GrundartikelLink code={preview.data.merchant_sku} /></b>
            <p>
              {preview.data.artikel_name}{' '}
              {preview.data.roll_length_m ? `R${preview.data.roll_length_m}` : ''}
            </p>
            <p>
              1 ganze Palette · {palletSourceText(preview.data.source_lager)} · Ziel Lager {preview.data.target_lager} ·{' '}
              {preview.data.has_stock
                ? preview.data.quantity === null
                  ? 'Rest unbekannt'
                  : `Rest ${preview.data.quantity}`
                : 'Wartet auf Quelle'}
            </p>
          </div>
        )}
        <Button
          disabled={!preview.data || preview.isFetching || pending || !enabled}
          onClick={() =>
            void onPalletRequest?.(code.trim(), roll).then((ok) => {
              setNotice(
                ok
                  ? 'Palettenauftrag bestätigt'
                  : 'Ergebnis prüfen — ursprünglichen Beleg behalten',
              )
              if (ok) setCode('')
            })
          }
        >
          Palette anfordern
        </Button>
        {notice && <p role="status">{notice}</p>}
      </DialogContent>
    </Dialog>
  )
}
